/**
 * 用户信息全局状态
 */
import { allAreaTree, getDictDataApi } from '@/apis/apiSystem.js';
import { getLatestMessage, getSchool, getUnreadMessageCount, memberUserGet, refreshToken } from '@/apis/apiUser.js';
import { useExpertsStore } from '@/stores/index.js';
import { setToken, setUserInfo } from '@/utils/auth.js';
import { useMutation } from '@tanstack/vue-query';
import axios from 'axios';
import { ElMessage } from 'element-plus';
import _ from 'lodash-es';
import { defineStore } from 'pinia';
import { ref } from 'vue';
import { useRoute, useRouter } from 'vue-router';

const useUserStore = defineStore('storeUser', () => {
  const role = ref('admin');
  const userInfo = ref({});
  const allArea = ref([]);
  const school = ref([]);
  const cloneInfo = ref({});
  const message = ref([]);
  const hasMessage = ref(false);
  const refreshRequest = ref([]);

  const router = useRouter();
  const route = useRoute();

  // 获取用户基本信息
  const { mutate: memberUserGetApi, isPending: memberUserGetApiPending } = useMutation({
    mutationKey: 'memberUserGet',
    mutationFn: memberUserGet,
    onSuccess: (data, values) => {
      if (!data) return;

      setUserInfo(data);
      userInfo.value = data;
      cloneInfo.value = _.cloneDeep(data);

      if (values?.isLogin) {
        ElMessage.success('登录成功');
        router.push(route.query.redirect || '/');
      }
    },
  });

  // 刷新令牌
  const { mutate: refreshTokenApi, isPending: refreshTokenApiPending } = useMutation({
    mutationKey: 'refreshToken',
    mutationFn: refreshToken,
    onSuccess: (data) => {
      if (!data)
        return;
      setToken(data);
      memberUserGetApi();
      localStorage.setItem('tb_token_expiresTime', data.expiresTime);

      // 重新请求已失效的请求
      if (refreshRequest.value) {
        const instance = axios.create();

        refreshRequest.value.forEach((item) => {
          const config = {
            ...item,
            headers: {
              ...item.headers,
              Authorization: `Bearer ${data.accessToken}`,
            },
          };

          instance(config);
        });

        refreshRequest.value = [];
      }
    },
  });

  // 获取地区树
  const { mutate: allAreaTreeApi } = useMutation({
    mutationKey: 'allAreaTree',
    mutationFn: allAreaTree,
    onSuccess: (data) => {
      if (!data)
        return;

      allArea.value = data;
      sessionStorage.setItem('tb_all_area', JSON.stringify(data));
    },
  });

  /**
   * 获取字典
   * @param {*} type - 字典类型
   * @returns {object} - 字典数据
   */
  const getDictDataApiByCode = async (type) => {
    const data = await getDictDataApi({ type });

    if (!data)
      return;

    return data;
  };

  /**
   * 获取学校数据
   * @param {*} type - 字典类型
   * @returns {object}
   */
  const { mutate: getSchoolApi } = useMutation({
    mutationKey: 'getSchool',
    mutationFn: getSchool,
    onSuccess: (data) => {
      if (!data)
        return;
      school.value = data;
      sessionStorage.setItem('tb_all_school', JSON.stringify(data));
    },
  });

  const getSchoolData = () => {
    const allSchoolStores = sessionStorage.getItem('tb_all_school');

    // 学校
    if (allSchoolStores) {
      school.value = JSON.parse(allSchoolStores);
    } else {
      getSchoolApi();
    }
  };

  /** 字典类型 */
  const dictTypes = [
    'technology_category', // 获取所有技术分类
    'champion_type', // 获取所有揭榜人类型
    'feedback_requirement', // 获取所有工作反馈要求
    'billing_type', // 获取所有开票类型
    'role_auth_type', // 获取所有角色
    'qualification', // 获取所有学历
    'academic_title', // 获取所有职称
    'enterprise_type', // 获取所有企业类型
    'sort_label', // 获取所有排序标签
    'course_category', // 获取所有课程分类
    'difficulty_level', // 获取所有难易程度
    'question_category', // 获取所有题目分类
    'question_type', // 获取所有题目类型
    'article_category', // 获取文章栏目分类
    'product_type', // 获取所有产品类型
    'teaching_train_channel', // 师资研修报名渠道
    'pay_method', // 支付方式
    'teaching_end_result', // 结业成果
    'invoice_type', // 发票获取方式
    'invoice_get_way', // 发票类型
    'graph_node_type', // 图谱节点类型
    'technical_expert_label', // 技术专家标签
    'business_expert_label', // 业务专家标签
    'course_service_type', // 课程服务类型
    'question_property', // 试题性质
    'examination_paper_type', // 试卷类型
    'exam_paper_scene', // 试卷应用场景
    'capability_training_type', // 能力特训分类
    'training_camp_source', // 特训营获悉渠道
    'application_category', // 数智应用类别
    'require_category', // 需求类别
    'require_apply_category', // 报名人类型
  ];

  /** 定义字典容器 */
  const dictRefs = Object.fromEntries(dictTypes.map(type => [type, ref([])]));
  const {
    technology_category,
    champion_type,
    feedback_requirement,
    billing_type,
    role_auth_type,
    qualification,
    academic_title,
    enterprise_type,
    sort_label,
    course_category,
    difficulty_level,
    question_category,
    question_type,
    article_category,
    product_type,
    teaching_train_channel,
    pay_method,
    teaching_end_result,
    invoice_type,
    invoice_get_way,
    graph_node_type,
    technical_expert_label,
    business_expert_label,
    course_service_type,
    question_property,
    examination_paper_type,
    exam_paper_scene,
    capability_training_type,
    training_camp_source,
    application_category,
    require_category,
    require_apply_category,
  } = dictRefs;

  /** 获取字典 区域 */
  const getDickApis = async () => {
    // 初始化dictRefs
    for (const key in dictRefs) {
      dictRefs[key].value = [];
    }

    let allCode;

    const allCodeStores = sessionStorage.getItem('tb_all_Code');
    const allAreaStores = sessionStorage.getItem('tb_all_area');

    // 字典
    if (allCodeStores && ((`${allAreaStores}`) !== 'undefined')) {
      allCode = JSON.parse(allCodeStores);
    } else {
      allCode = await getDictDataApiByCode(dictTypes.join(','));
      sessionStorage.setItem('tb_all_Code', JSON.stringify(allCode));
    }

    allCode?.forEach((code) => {
      dictRefs[code.dictType].value.push(code);
    });

    // 区域
    if (allAreaStores) {
      allArea.value = JSON.parse(allAreaStores);
    } else {
      allAreaTreeApi();
    }

    // 更新专家标签
    const expertsStore = useExpertsStore();

    expertsStore.expertTagsEnum = {
      technical: technical_expert_label || [],
      business: business_expert_label || [],
    };
  };

  // 获取当前用户的最新站内信列表
  const { mutate: getLatestMessageFn } = useMutation({
    mutationFn: getLatestMessage,
    onSuccess: async (val) => {
      if (!val)
        return;
      message.value = val;
    },
  });
  // 获取消息数量
  const { mutate: userAuthenticationFn } = useMutation({
    mutationFn: getUnreadMessageCount,
    onSuccess: async (val) => {
      if (!val) {
        hasMessage.value = false;
      } else {
        hasMessage.value = true;
      }
    },
  });

  // 重置
  const storeReset = () => {
    userInfo.value = {};
    sessionStorage.removeItem('tb_all_Code');
    sessionStorage.removeItem('tb_all_school');
    sessionStorage.removeItem('tb_all_area');

    const expertsStore = useExpertsStore();

    expertsStore.userExpertInfo = false;
  };

  return {
    role,
    userInfo,
    technology_category,
    champion_type,
    feedback_requirement,
    billing_type,
    role_auth_type,
    qualification,
    academic_title,
    allArea,
    enterprise_type,
    sort_label,
    course_category,
    difficulty_level,
    question_category,
    question_type,
    article_category,
    product_type,
    teaching_train_channel,
    pay_method,
    teaching_end_result,
    invoice_type,
    invoice_get_way,
    graph_node_type,
    technical_expert_label,
    business_expert_label,
    course_service_type,
    question_property,
    examination_paper_type,
    exam_paper_scene,
    capability_training_type,
    training_camp_source,
    application_category,
    require_category,
    require_apply_category,
    school,
    cloneInfo,
    message,
    hasMessage,
    refreshRequest,
    memberUserGetApi,
    memberUserGetApiPending,
    refreshTokenApi,
    refreshTokenApiPending,
    getDickApis,
    getSchoolData,
    getLatestMessageFn,
    userAuthenticationFn,
    storeReset,

  };
});

export default useUserStore;
